import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { restaurantTableService } from '../../../../services/restaurantTableService';
import { RefreshCw, ArrowLeft, LayoutGrid, Save } from 'lucide-react';
import '../RestaurantPage.css';

const STATUS_COLOR: Record<string, { bg: string; border: string; text: string }> = {
  Available: { bg: 'var(--ims-green-bg)', border: 'var(--ims-green-text)', text: 'var(--ims-green-text)' },
  Occupied:  { bg: 'var(--ims-red-bg)',   border: 'var(--ims-red-text)',   text: 'var(--ims-red-text)' },
  Reserved:  { bg: 'var(--ims-amber-bg)', border: 'var(--ims-amber-text)', text: 'var(--ims-amber-text)' },
};

interface TableItem {
  id: number; tableNumber: string; name: string; capacity: number;
  section?: string; shape: string; positionX: number; positionY: number;
  width: number; height: number; liveStatus: string; currentOrderNumber?: string;
  isActive?: boolean;
}

interface DragState {
  tableId: number; startMouseX: number; startMouseY: number;
  startPosX: number; startPosY: number;
}

const CANVAS_W = 900;
const CANVAS_H = 600;

const FloorPlanPage: React.FC = () => {
  const navigate    = useNavigate();
  const { t }       = useTranslation();
  const canvasRef   = useRef<HTMLDivElement>(null);
  const dragRef     = useRef<DragState | null>(null);

  const [tables,    setTables]    = useState<TableItem[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState<number | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [dirty,     setDirty]     = useState<Set<number>>(new Set());

  const loadTables = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await restaurantTableService.getAll();
      const d = res?.data?.data;
      const arr: TableItem[] = Array.isArray(d) ? d : (d as any)?.data ?? [];
      setTables(arr.filter((tbl: TableItem) => tbl.isActive !== false));
    } catch {
      setError(t('table.failedToLoad', 'Failed to load tables.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadTables(); }, [loadTables]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>, tableId: number) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setTables(prev => {
      const tbl = prev.find(t => t.id === tableId);
      if (!tbl) return prev;
      dragRef.current = {
        tableId,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startPosX: tbl.positionX,
        startPosY: tbl.positionY,
      };
      return prev;
    });
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const { tableId, startMouseX, startMouseY, startPosX, startPosY } = dragRef.current;
    const dx = e.clientX - startMouseX;
    const dy = e.clientY - startMouseY;
    setTables(prev => prev.map(t =>
      t.id === tableId
        ? { ...t, positionX: Math.max(0, startPosX + dx), positionY: Math.max(0, startPosY + dy) }
        : t
    ));
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const { tableId } = dragRef.current;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDirty(prev => new Set(prev).add(tableId));
  }, []);

  const saveAll = async () => {
    const toSave = tables.filter(t => dirty.has(t.id));
    for (const tbl of toSave) {
      setSaving(tbl.id);
      try {
        await restaurantTableService.updatePosition(tbl.id, { positionX: tbl.positionX, positionY: tbl.positionY });
      } catch {
        setError(t('table.failedToSave', 'Failed to save position.'));
      } finally {
        setSaving(null);
      }
    }
    setDirty(new Set());
  };

  const renderTable = (tbl: TableItem) => {
    const colors = STATUS_COLOR[tbl.liveStatus] ?? STATUS_COLOR.Available;
    const isRound  = tbl.shape === 'Round';
    const isSaving = saving === tbl.id;
    return (
      <div
        key={tbl.id}
        onPointerDown={(e) => onPointerDown(e, tbl.id)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position: 'absolute',
          left: tbl.positionX, top: tbl.positionY,
          width: tbl.width, height: tbl.height,
          background: colors.bg,
          border: `2px solid ${colors.border}`,
          borderRadius: isRound ? '50%' : 6,
          cursor: dragRef.current?.tableId === tbl.id ? 'grabbing' : 'grab',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          userSelect: 'none',
          touchAction: 'none',
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          transition: dragRef.current?.tableId === tbl.id ? 'none' : 'box-shadow 0.15s',
        }}
        title={`${tbl.name} — ${tbl.liveStatus}${tbl.currentOrderNumber ? ` (${tbl.currentOrderNumber})` : ''}`}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: colors.text, lineHeight: 1.2, pointerEvents: 'none' }}>{tbl.tableNumber}</span>
        <span style={{ fontSize: 9, color: colors.text, opacity: 0.75, pointerEvents: 'none' }}>{tbl.capacity}p</span>
        {isSaving && <span style={{ fontSize: 8, color: colors.text, pointerEvents: 'none' }}>…</span>}
      </div>
    );
  };

  return (
    <Layout>
      <div className="ims-page rest-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('table.floorPlan')}</h1>
            <p className="ims-list-subtitle">{t('table.floorPlanDesc', 'Drag tables to rearrange the floor layout')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={loadTables} disabled={loading}>
              <RefreshCw size={15} className={loading ? 'refresh-icon-spinning' : ''} />
              {loading ? t('common.loading') : t('common.refresh')}
            </button>
            {dirty.size > 0 && (
              <button className="btn-new" onClick={saveAll} disabled={saving !== null}>
                <Save size={16} /> {t('common.save')} ({dirty.size})
              </button>
            )}
            <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/restaurant/tables')}>
              <ArrowLeft size={14} /> {t('table.title')}
            </button>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_COLOR).map(([status, colors]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 14, height: 14, borderRadius: 3, background: colors.bg, border: `2px solid ${colors.border}` }} />
              <span style={{ fontSize: 12, color: 'var(--ims-text-sub)' }}>{t(`table.liveStatus.${status}`, status)}</span>
            </div>
          ))}
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 12 }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {dirty.size > 0 && (
          <div className="ims-alert ims-alert-warning" style={{ marginBottom: 12 }}>
            {t('table.unsavedPositions', 'You have unsaved position changes. Click Save to persist them.')}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border" role="status" />
          </div>
        ) : (
          <div className="ims-card" style={{ padding: 0, overflow: 'auto' }}>
            <div
              ref={canvasRef}
              style={{
                position: 'relative', width: CANVAS_W, height: CANVAS_H,
                background: 'repeating-linear-gradient(0deg,transparent,transparent 29px,var(--ims-border) 29px,var(--ims-border) 30px),repeating-linear-gradient(90deg,transparent,transparent 29px,var(--ims-border) 29px,var(--ims-border) 30px)',
                backgroundSize: '30px 30px', borderRadius: 6,
                minWidth: CANVAS_W,
              }}
            >
              {tables.map(renderTable)}
              {tables.length === 0 && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
                  <LayoutGrid size={48} strokeWidth={1} style={{ color: 'var(--ims-text-muted)' }} />
                  <p style={{ color: 'var(--ims-text-muted)' }}>{t('table.noTablesFound', 'No tables found.')}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FloorPlanPage;
